import { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { User, ChevronDown, LogOut, Lock } from 'lucide-react'
import type { UserProfile } from '@/services/api'
import { NotificationBell } from '@/shared/components/NotificationBell' // Ajusta la ruta si la guardaste en otro lado

interface HeaderProps {
  user?: UserProfile | null
  onLogout?: () => void
}

const Header = ({ user, onLogout }: HeaderProps) => {
  const navigate = useNavigate()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  return (
    <header
      className="h-16 border-b border-gray-200 px-6 flex items-center justify-between sticky top-0 z-20"
      style={{ backgroundColor: '#FFFFFF' }}
    >
      {/* Left - Search Bar (Empty for now) */}
      <div className="flex items-center flex-1 max-w-md mr-4">
        <div className="relative w-full">
          {/* Espacio reservado para buscador */}
        </div>
      </div>

      {/* Right - Notifications & User Menu */}
      <div className="flex items-center gap-4 flex-shrink-0">

        <NotificationBell />

        {/* User Menu */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={toggleMenu}
            className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 rounded-lg transition-colors !bg-transparent"
            style={{ backgroundColor: 'transparent', color: 'inherit' }}
          >
            <div className="w-9 h-9 rounded-full bg-gray-300 flex items-center justify-center">
              <User className="w-5 h-5 text-gray-600" />
            </div>
            {user && (
              <div className="hidden md:block text-left">
                <div className="text-sm font-semibold text-gray-900 leading-tight">
                  {user.firstName} {user.lastName}
                </div>
                <div className="text-xs text-gray-500 font-medium">
                  {user?.roles?.[0]?.name ?? ''}
                </div>
              </div>
            )}
            <ChevronDown
              className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${isMenuOpen ? 'rotate-180' : ''
                }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isMenuOpen && (
            <div className="absolute right-0 mt-2 w-56 rounded-xl border border-gray-200 shadow-xl py-2 z-50" style={{ backgroundColor: '#FFFFFF' }}>
              {/* User Info — lleva a la consulta del propio usuario */}
              {user && (
                <button
                  onClick={() => {
                    setIsMenuOpen(false)
                    navigate(`/users/${user.id}`)
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                  style={{ borderBottom: '1px solid #E7E7E8' }}
                >
                  <p className="text-sm font-semibold text-gray-900">
                    {user.firstName} {user.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize mt-0.5">{user?.roles?.[0]?.name ?? ''}</p>
                </button>
              )}

              {/* Cambiar contraseña */}
              <button
                onClick={() => {
                  setIsMenuOpen(false)
                  navigate('/cambiar-contrasena')
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left text-gray-700"
              >
                <Lock className="w-4 h-4" />
                <span className="text-sm font-medium">Cambiar contraseña</span>
              </button>

              {/* Logout */}
              {onLogout && (
                <button
                  onClick={onLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-red-50 transition-colors text-left text-red-600 mt-1"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="text-sm font-medium">Cerrar sesión</span>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
